import { useUpdateMarketplaceDashboardPublication } from "@/api/hooks/marketplace";
import { useUser } from "@/api/hooks/user";
import logo from "@/logo-ncm.png";
import { useMarketplaceImage } from "@/marketplace/hooks";
import "@/marketplace/marketplace-dashboard-single/marketplace-dashboard-single.scss";
import { getMarketplaceTierTagColor, isMarketplaceEditor } from "@/marketplace/utils";
import { MeteomaticsLogo } from "@/meteomatics-logo";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, Tag } from "@mm/metx-design-system";
import type { MarketplaceDashboardDetail } from "@mm/metx-workbench.meteomatics.com";
import { useState } from "react";
import { Link } from "react-router-dom";

type MarketplaceDashboardSingleProps = {
  dashboard: MarketplaceDashboardDetail;
  marketplaceHref: string;
  showCustomLogo?: boolean;
  onAddDashboard: () => void;
  isAdding: boolean;
  showAdded: boolean;
};

export function MarketplaceDashboardSingle({
  dashboard,
  marketplaceHref,
  showCustomLogo,
  onAddDashboard,
  isAdding,
  showAdded,
}: MarketplaceDashboardSingleProps) {
  const { user } = useUser();
  const canEditMarketplace = isMarketplaceEditor(user);
  const updatePublication = useUpdateMarketplaceDashboardPublication();
  const [isPublishing, setIsPublishing] = useState(false);
  const imageSrc = useMarketplaceImage(dashboard.image_url);
  const isPublished = dashboard.published ?? false;
  const addLabel = showAdded
    ? t({ id: "marketplace.added", message: "Added" })
    : t({ id: "marketplace.add-dashboard", message: "Add dashboard" });

  const handleTogglePublication = async () => {
    if (isPublishing) {
      return;
    }
    setIsPublishing(true);
    try {
      await updatePublication.mutateAsync({
        profileId: dashboard.id,
        publication: { published: !isPublished },
      });
    } catch (error) {
      console.error("Failed to update marketplace publication:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="marketplace-detail page cover-window">
      <header className="marketplace-detail__header">
        <div className="marketplace-detail__header-start">
          <div className="marketplace-detail__logo">
            {showCustomLogo ? <img height="32" src={logo} alt="logo ncm" /> : <MeteomaticsLogo variant="dark" />}
          </div>
          <nav className="marketplace-detail__breadcrumb" aria-label="Breadcrumb">
            <Link className="marketplace-detail__breadcrumb-link" to={marketplaceHref}>
              <span className="material-symbols-outlined marketplace-detail__breadcrumb-icon-left" aria-hidden>
                arrow_back_ios
              </span>
              <Trans id="marketplace.title">Marketplace</Trans>
            </Link>
            <span className="material-symbols-outlined marketplace-detail__breadcrumb-icon-right" aria-hidden>
              arrow_forward_ios
            </span>
            <span className="marketplace-detail__breadcrumb-current">{dashboard.title}</span>
          </nav>
        </div>
        <Button variant="primary" disabled={showAdded || isAdding} onClick={onAddDashboard}>
          {showAdded
            ? t({ id: "marketplace.added", message: "Added" })
            : t({
                id: "marketplace.add-to-my-dashboards",
                message: "Add to My Dashboards",
              })}
        </Button>
      </header>

      {imageSrc && (
        <div className="marketplace-detail__hero">
          <img className="marketplace-detail__hero-image" src={imageSrc} alt="" />
        </div>
      )}

      <div className="marketplace-detail__content">
        <div className="marketplace-detail__main">
          <section className="marketplace-detail__card">
            <h1 className="marketplace-detail__title">{dashboard.title}</h1>
            {dashboard.description && <p className="marketplace-detail__description">{dashboard.description}</p>}
          </section>

          {dashboard.included.length > 0 && (
            <section className="marketplace-detail__card">
              <h2 className="marketplace-detail__section-title">
                <Trans id="marketplace.whats-included">What's included</Trans>
              </h2>
              <ul className="marketplace-detail__included-list">
                {dashboard.included.map((item) => (
                  <li key={item} className="marketplace-detail__included-item">
                    <span className="material-symbols-outlined marketplace-detail__included-icon" aria-hidden>
                      check
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="marketplace-detail__sidebar">
          <section className="marketplace-detail__card marketplace-detail__info-card">
            <h2 className="marketplace-detail__section-title">
              <Trans id="marketplace.dashboard-information">Dashboard Information</Trans>
            </h2>
            <dl className="marketplace-detail__info-list">
              {dashboard.author && (
                <div className="marketplace-detail__info-row">
                  <dt>
                    <Trans id="marketplace.info.author">Author</Trans>
                  </dt>
                  <dd>{dashboard.author}</dd>
                </div>
              )}
              {dashboard.total_tabs > 0 && (
                <div className="marketplace-detail__info-row">
                  <dt>
                    <Trans id="marketplace.info.total-tabs">Total Tabs</Trans>
                  </dt>
                  <dd>{dashboard.total_tabs}</dd>
                </div>
              )}
              {dashboard.data_sources.length > 0 && (
                <div className="marketplace-detail__info-row">
                  <dt>
                    <Trans id="marketplace.info.data-sources">Data sources</Trans>
                  </dt>
                  <dd>{dashboard.data_sources.join(", ")}</dd>
                </div>
              )}
              {dashboard.tier && (
                <div className="marketplace-detail__info-row">
                  <dt>
                    <Trans id="marketplace.info.tag">Plan</Trans>
                  </dt>
                  <dd>
                    <Tag color={getMarketplaceTierTagColor(dashboard.tier)}>{dashboard.tier}</Tag>
                  </dd>
                </div>
              )}
            </dl>
            <div className="marketplace-detail__actions">
              <Button variant="primary" fullWidth disabled={showAdded || isAdding} onClick={onAddDashboard}>
                {addLabel}
              </Button>
              {canEditMarketplace && (
                <Button
                  variant={isPublished ? "outline" : "secondary"}
                  fullWidth
                  loading={isPublishing}
                  disabled={isPublishing}
                  leftIcon={
                    <span className="material-symbols-outlined" aria-hidden>
                      {isPublished ? "block" : "publish"}
                    </span>
                  }
                  onClick={() => void handleTogglePublication()}
                >
                  {isPublished ? t`Unpublish` : t`Publish`}
                </Button>
              )}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
